import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Search, Filter, ExternalLink, ChevronDown, ChevronUp, FileText, Globe } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TYPE_COLORS = { legislation: "#ef4444", strategy: "#3b82f6", guideline: "#a855f7", standard: "#10b981", program: "#f59e0b", "governance-body": "#06b6d4" };
const TYPE_LABELS = { legislation: "Legislation", strategy: "Strategy", guideline: "Guideline", standard: "Standard", program: "Program", "governance-body": "Governance Body" };
const ENF_COLORS = { mandatory: "#ef4444", voluntary: "#f59e0b", advisory: "#3b82f6" };
const ORIENT_COLORS = { care: "#10b981", utility: "#3b82f6", creative: "#a855f7", extraction: "#ef4444" };

export const PolicyExplorer = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterEnf, setFilterEnf] = useState("");
  const [filterRegion, setFilterRegion] = useState("");
  const [filterOrient, setFilterOrient] = useState("");
  const [filterSector, setFilterSector] = useState("");
  const [sortBy, setSortBy] = useState("policy_cii");
  const [sortOrder, setSortOrder] = useState("desc");
  const [expandedPolicy, setExpandedPolicy] = useState(null);

  useEffect(() => {
    axios.get(`${API}/policy/list?limit=500`).then(r => setPolicies(r.data.policies || [])).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return policies.filter(p => {
      if (filterType && p.policy_type !== filterType) return false;
      if (filterEnf && p.enforcement_level !== filterEnf) return false;
      if (filterRegion && p.region !== filterRegion) return false;
      if (filterOrient && p.value_orientation !== filterOrient) return false;
      if (filterSector && p.sector !== filterSector) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!p.policy_name.toLowerCase().includes(q) && !p.country.toLowerCase().includes(q)) return false;
      }
      return true;
    }).sort((a, b) => {
      const dir = sortOrder === "desc" ? -1 : 1;
      if (sortBy === "policy_cii") return dir * (a.policy_cii - b.policy_cii);
      if (sortBy === "year_adopted") return dir * (a.year_adopted - b.year_adopted);
      if (sortBy === "policy_name") return dir * a.policy_name.localeCompare(b.policy_name);
      if (sortBy === "country") return dir * a.country.localeCompare(b.country);
      return 0;
    });
  }, [policies, filterType, filterEnf, filterRegion, filterOrient, filterSector, searchQuery, sortBy, sortOrder]);

  const regions = useMemo(() => [...new Set(policies.map(p => p.region))].sort(), [policies]);
  const sectors = useMemo(() => [...new Set(policies.map(p => p.sector))].sort(), [policies]);

  if (loading) return <div className="flex items-center justify-center py-32"><div className="w-10 h-10 rounded-full border-2 border-cyan-500/30 border-t-cyan-500 animate-spin" /></div>;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="policy-explorer-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-black text-white">Policy Explorer</h2>
          <p className="text-xs text-slate-400">{filtered.length} of {policies.length} policies</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-48">
            <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search policies or countries..." className="w-full pl-8 pr-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/30" data-testid="policy-search-input" />
          </div>
          <select value={filterOrient} onChange={e => setFilterOrient(e.target.value)} className="px-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white" data-testid="filter-orient">
            <option value="">All Orientations</option>
            <option value="care">Care</option><option value="utility">Utility</option><option value="creative">Creative</option><option value="extraction">Extraction</option>
          </select>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} className="px-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white" data-testid="filter-type">
            <option value="">All Types</option>
            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={filterEnf} onChange={e => setFilterEnf(e.target.value)} className="px-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white" data-testid="filter-enf">
            <option value="">All Enforcement</option>
            <option value="mandatory">Mandatory</option><option value="voluntary">Voluntary</option><option value="advisory">Advisory</option>
          </select>
          <select value={filterRegion} onChange={e => setFilterRegion(e.target.value)} className="px-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white" data-testid="filter-region">
            <option value="">All Regions</option>
            {regions.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <select value={filterSector} onChange={e => setFilterSector(e.target.value)} className="px-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white" data-testid="filter-sector">
            <option value="">All Sectors</option>
            {sectors.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-white/[0.04]">
          <span className="text-[10px] text-slate-500">Sort:</span>
          {[["policy_cii","CII Score"],["year_adopted","Year"],["country","Country"],["policy_name","Name"]].map(([val,label]) => (
            <button key={val} onClick={() => { if (sortBy === val) setSortOrder(o => o === "desc" ? "asc" : "desc"); else { setSortBy(val); setSortOrder("desc"); }}} data-testid={`sort-${val}`}
              className={`text-[10px] px-2 py-1 rounded ${sortBy === val ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/30" : "text-slate-400 hover:text-white"}`}>
              {label} {sortBy === val && (sortOrder === "desc" ? "\u2193" : "\u2191")}
            </button>
          ))}
        </div>
      </div>

      {/* Policy List */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden divide-y divide-white/[0.03]">
        {filtered.length === 0 && <div className="text-center py-12"><FileText className="w-10 h-10 text-slate-700 mx-auto mb-2" /><p className="text-sm text-slate-400">No policies match your filters</p></div>}
        {filtered.map((p, i) => {
          const isExpanded = expandedPolicy === i;
          const cColor = p.policy_cii >= 60 ? "#10b981" : p.policy_cii >= 30 ? "#f59e0b" : "#ef4444";
          return (
            <div key={`${p.policy_name}-${p.country}-${i}`} className="hover:bg-white/[0.02] transition-colors">
              <div className="px-5 py-3 flex items-center gap-4 cursor-pointer" onClick={() => setExpandedPolicy(isExpanded ? null : i)} data-testid={`policy-row-${i}`}>
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: ORIENT_COLORS[p.value_orientation] || "#64748b" }} title={p.value_orientation} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white truncate">{p.policy_name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Globe className="w-3 h-3 text-slate-500" />
                    <span className="text-[10px] text-slate-400">{p.country}</span>
                    <span className="text-[10px] text-slate-600">&middot;</span>
                    <span className="text-[10px] text-slate-400">{p.year_adopted}</span>
                    {p.sector !== "cross-sector" && <><span className="text-[10px] text-slate-600">&middot;</span><span className="text-[10px] text-slate-400">{p.sector}</span></>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[9px] px-2 py-0.5 rounded-full capitalize" style={{ color: ORIENT_COLORS[p.value_orientation], background: `${ORIENT_COLORS[p.value_orientation]}15`, border: `1px solid ${ORIENT_COLORS[p.value_orientation]}25` }}>{p.value_orientation}</span>
                  <span className="text-[9px] px-2 py-0.5 rounded-full" style={{ color: TYPE_COLORS[p.policy_type], background: `${TYPE_COLORS[p.policy_type]}15`, border: `1px solid ${TYPE_COLORS[p.policy_type]}25` }}>{TYPE_LABELS[p.policy_type] || p.policy_type}</span>
                  <span className="text-[9px] px-2 py-0.5 rounded-full" style={{ color: ENF_COLORS[p.enforcement_level], background: `${ENF_COLORS[p.enforcement_level]}15`, border: `1px solid ${ENF_COLORS[p.enforcement_level]}25` }}>{p.enforcement_level}</span>
                  <span className="text-sm font-black w-10 text-right" style={{ color: cColor }}>{p.policy_cii}</span>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                </div>
              </div>
              {isExpanded && (
                <div className="px-5 pb-4 space-y-3 animate-fade-in">
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    <div className="text-[10px]"><span className="text-slate-500">Scope:</span> <span className="text-white">{p.scope}</span></div>
                    <div className="text-[10px]"><span className="text-slate-500">Region:</span> <span className="text-white">{p.region}</span></div>
                    <div className="text-[10px]"><span className="text-slate-500">Sector:</span> <span className="text-white">{p.sector}</span></div>
                    <div className="text-[10px]"><span className="text-slate-500">Status:</span> <span className="text-white capitalize">{p.status}</span></div>
                    <div className="text-[10px]"><span className="text-slate-500">Source:</span> <span className="text-white">{p.source}</span></div>
                  </div>
                  <div><p className="text-[10px] text-slate-500 mb-1">Areas Covered:</p>
                    <div className="flex flex-wrap gap-1">{(p.areas_covered || []).map(a => <span key={a} className="text-[9px] px-2 py-0.5 bg-cyan-500/10 border border-cyan-500/15 rounded-full text-cyan-300">{a}</span>)}</div>
                  </div>
                  <div><p className="text-[10px] text-slate-500 mb-1">CII Provisions (7 Dimensions):</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                      {[["care_provisions","Care"],["diversity_provisions","Diversity"],["governance_provisions","Governance"],["geographic_equity","Geographic Eq."],["extraction_restrictions","Extract. Restrict."],["inclusion_mandates","Inclusion"],["agency_protections","Agency"]].map(([key,label]) => (
                        <div key={key} className={`text-[9px] px-2 py-1 rounded border ${p[key] ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-300" : "bg-white/[0.02] border-white/[0.04] text-slate-500"}`}>{p[key] ? "\u2713" : "\u2717"} {label}</div>
                      ))}
                    </div>
                  </div>
                  {p.source_url && <a href={p.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] text-cyan-400 hover:text-cyan-300">View source <ExternalLink className="w-3 h-3" /></a>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
